import {
    deepKeysToSnake,
    keysCamelToSnake,
} from '@eventbrite/transformation-utils';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import packageJson from '../../../package.json';
import { getTokens } from '../../actions/tokens';
import {
    PAYMENT_IDENTIFICATION_METHOD,
    PAYMENT_IDENTIFICATION_NUMBER,
} from '../../components/paymentMethodForms/constants';
import { PAYMENT_METHODS, SQ_ANALYTICS_TOKEN } from '../../constants';
import {
    CARD_EXPIRATION_DATE_FIELD,
    CARD_POSTAL_CODE_FIELD,
    CARD_SECURITY_CODE_FIELD,
    CREDIT_CARD_NUMBER_FIELD,
    SAVE_CREDIT_CARD_FIELD,
} from '../../containers/checkout/constants';
import { isIdentificationRequiredForCurrency } from '../../models/currency';
import {
    InstrumentType,
    PaymentMethodType,
} from '../../models/paymentMethods/enums';
import { UserInstrument, VirtualIncentive } from '../../models/user';
import { PAYMENT_METHOD_CLEAR_OPERATION } from '../../utils/resiliencyFeatureFlag';
import { getSelectedVirtualIncentivesAggregatedTotal } from '../../utils/virtualIncentives';
import { getOrderFlowHeaders } from '../base';
import {
    _getClearPaymentMethodUrl,
    _getSavePaymentMethodUrl,
} from '../constants';
import { _fetchV3WithOrderErrorHandling } from './errors';

interface IdealPaymentData {
    bankId: string;
}

const _payloadForIdeal = (payment: IdealPaymentData) => ({
    payment_methods: [
        {
            payment_instrument_details: {
                instrument_type: 'BANK',
                payment_method: PAYMENT_METHODS.IDEAL,
                bank_id: payment.bankId,
            },
        },
    ],
});

interface ApplePayPaymentData {
    applePayBraintreeNonce: string;
}

const _payloadForApplePay = (payment: ApplePayPaymentData) => ({
    payment_methods: [
        {
            payment_instrument_details: {
                nonce: payment.applePayBraintreeNonce,
                instrument_type: 'NONCE_BRAINTREE',
            },
        },
    ],
});

interface GooglePayPaymentData {
    googlePayBraintreeNonce: string;
}

const _payloadForGooglePay = (payment: GooglePayPaymentData) => ({
    payment_methods: [
        {
            payment_instrument_details: {
                nonce: payment.googlePayBraintreeNonce,
                instrument_type: 'NONCE_BRAINTREE',
            },
        },
    ],
});

interface PaypalEPPPaymentData {
    paypalBraintreeNonce: string;
}

const _payloadForNonceBraintree = (payment: PaypalEPPPaymentData) => ({
    payment_methods: [
        {
            payment_instrument_details: {
                nonce: payment.paypalBraintreeNonce,
                instrument_type: 'NONCE_BRAINTREE',
            },
        },
    ],
});

interface StripeConfirmationTokenPaymentData {
    confirmationTokenID: string;
}

const _payloadForStripeConfirmationToken = (
    payment: StripeConfirmationTokenPaymentData,
) => ({
    payment_methods: [
        {
            payment_instrument_details: {
                confirmation_token_id: payment.confirmationTokenID,
                instrument_type: PaymentMethodType.STRIPE_CONFIRMATION_TOKEN,
            },
        },
    ],
});

/**
 * Reformats expiration date input from MM/YY to MMYY,
 * how order service is expecting to receive it.
 */
const _reformatCardExpirationDate = (expirationDate?: string) => {
    if (expirationDate && expirationDate.indexOf('/') >= 0) {
        let [month, year] = expirationDate.split('/');

        // Pad month with a leading 0 if it's only one digit. We allow
        // users to input M/YY but the backend always expects MM/YY.
        month = month.length === 1 ? `0${month}` : month;
        return [month, year].join('');
    }
    return undefined;
};

interface BancontactPaymentData {
    creditCardNumber: string;
    cardExpirationDate: string;
}

const _payloadForMaestroBancontact = (bancontact?: BancontactPaymentData) => {
    let cardNumber;
    let expirationDate;

    if (bancontact) {
        cardNumber = bancontact.creditCardNumber;
        expirationDate = _reformatCardExpirationDate(
            bancontact.cardExpirationDate,
        );
    }

    const creditCardData = keysCamelToSnake({
        cardNumber,
        expirationDate,
    });

    return {
        payment_methods: [
            {
                payment_instrument_details: {
                    instrument_type: 'CREDIT',
                    payment_method: PAYMENT_METHODS.MAESTRO_BANCONTACT,
                    ...creditCardData,
                },
            },
        ],
    };
};

const _payloadForSofort = () => ({
    payment_methods: [
        {
            payment_instrument_details: {
                instrument_type: 'BANK',
                payment_method: PAYMENT_METHODS.SOFORT,
            },
        },
    ],
});

interface BoletoBancarioPaymentData {
    identificationNumber: string;
    identificationMethod: string;
}

const _payloadForBoletoBancario = ({
    identificationNumber,
    identificationMethod,
}: BoletoBancarioPaymentData) => ({
    payment_methods: [
        {
            payment_instrument_details: {
                instrument_type: PAYMENT_METHODS.BOLETO_BANCARIO,
                tax_identifier: identificationNumber,
                tax_identifier_type: identificationMethod,
            },
        },
    ],
});

interface BarcodePaymentData {
    variant: string;
    identificationNumber: string;
}

const _payloadForBarcode = ({
    variant,
    identificationNumber,
}: BarcodePaymentData) => ({
    payment_methods: [
        {
            payment_instrument_details: {
                instrument_type: PAYMENT_METHODS.BARCODE,
                payment_method: variant,
                tax_identifier: identificationNumber,
            },
        },
    ],
});

const _defaultPayload = (paymentType: PaymentMethodType) => ({
    payment_methods: [
        {
            payment_instrument_details: {
                instrument_type: paymentType,
                payment_method: paymentType,
            },
        },
    ],
});

interface SepaPaymentData {
    ibanAgreement: boolean;
    ownerName: string;
    iban: string;
}

const _payloadForSepa = (payment?: SepaPaymentData) => {
    let sepaData;

    if (payment && payment.ibanAgreement) {
        const ownerName = payment.ownerName;
        const iban = payment.iban;

        sepaData = keysCamelToSnake({
            ownerName,
            iban,
        });
    }

    return {
        payment_methods: [
            {
                payment_instrument_details: {
                    instrument_type: 'BANK_ACCOUNT_SEPA',
                    payment_method: PAYMENT_METHODS.SEPA_DIRECT_DEBIT,
                    ...sepaData,
                },
            },
        ],
    };
};

interface CreditCardPaymentData {
    [CARD_POSTAL_CODE_FIELD]: string;
    [PAYMENT_IDENTIFICATION_NUMBER]: string;
    [PAYMENT_IDENTIFICATION_METHOD]: string;
    [CREDIT_CARD_NUMBER_FIELD]: string;
    [CARD_SECURITY_CODE_FIELD]: string;
    [CARD_EXPIRATION_DATE_FIELD]: string;
    [SQ_ANALYTICS_TOKEN]: string;
    [SAVE_CREDIT_CARD_FIELD]: string;
    cardholder: string;
    selectIssuer: number;
    installments: string;
}

const _buildBillingInformation = (
    countryCode: string,
    {
        [CARD_POSTAL_CODE_FIELD]: postalCode,
        [PAYMENT_IDENTIFICATION_NUMBER]: number,
        [PAYMENT_IDENTIFICATION_METHOD]: identificationType,
    }: Pick<
        CreditCardPaymentData,
        'cardPostalCode' | 'identificationNumber' | 'identificationMethod'
    >,
    currency: string,
) => {
    const result = {
        address: keysCamelToSnake({
            postalCode,
            // XXX: countryCode is currently based on event venue, or TLD as
            // default. It should be changed to user billing information once
            // that data is collected
            country: countryCode,
        }),
    } as {
        address: Record<string, string>;
        identification?: Record<string, string>;
    };

    if (
        isIdentificationRequiredForCurrency[
            currency as keyof typeof isIdentificationRequiredForCurrency
        ]
    ) {
        result.identification = keysCamelToSnake({
            number,
            identificationType,
        });
    }

    return result;
};

const currenciesWithExtraParams = ['BRL', 'ARS', 'MXN'];

export const formatCreditCardInstrument = ({
    countryCode,
    payment,
    currency,
    instrumentType,
}: {
    countryCode: string;
    payment?: CreditCardPaymentData;
    currency: string;
    instrumentType: PaymentMethodType;
}) => {
    let cardNumber;
    let cvv;
    let expirationDate;
    let analyticsToken;
    let billingInformation = {
        address: {},
    };
    const extraParams: Record<string, unknown> = {};

    if (payment) {
        cardNumber = payment[CREDIT_CARD_NUMBER_FIELD];
        cvv = payment[CARD_SECURITY_CODE_FIELD];
        expirationDate = _reformatCardExpirationDate(
            payment[CARD_EXPIRATION_DATE_FIELD],
        );
        billingInformation = _buildBillingInformation(
            countryCode,
            payment,
            currency,
        );
        analyticsToken = payment[SQ_ANALYTICS_TOKEN]
            ? payment[SQ_ANALYTICS_TOKEN]
            : null;

        if (currenciesWithExtraParams.includes(currency)) {
            extraParams.cardholderName = payment.cardholder;
            if (payment.selectIssuer > 0) {
                extraParams.issuingBank = payment.selectIssuer;
                if (payment.installments) {
                    extraParams.installments = payment.installments;
                }
            }
        }
    }

    const instrument = keysCamelToSnake({
        instrumentType,
        cvv,
        cardNumber,
        expirationDate,
        analyticsToken,
        ...extraParams,
    });

    return {
        instrument,
        billingInformation,
    };
};

export const _payloadForCredit = ({
    countryCode,
    payment,
    currency,
    isNOS,
}: {
    countryCode: string;
    payment?: CreditCardPaymentData;
    currency: string;
    isNOS?: boolean;
}) => {
    const { instrument, billingInformation } = formatCreditCardInstrument({
        countryCode,
        payment,
        currency,
        instrumentType: PAYMENT_METHODS.CREDIT,
    });

    const keepInVault = {
        keep_in_vault: !!payment?.[SAVE_CREDIT_CARD_FIELD],
    };

    return {
        payment_methods: [
            {
                payment_instrument_details: instrument,
                billing_information: billingInformation,
                ...(isNOS && keepInVault),
            },
        ],
    };
};

const _payloadForAuthnet = ({
    countryCode,
    payment,
    currency,
}: {
    countryCode: string;
    payment?: CreditCardPaymentData;
    currency: string;
}) => {
    const { instrument, billingInformation } = formatCreditCardInstrument({
        countryCode,
        payment,
        currency,
        instrumentType: PAYMENT_METHODS.AUTHNET,
    });

    return {
        payment_methods: [
            {
                payment_instrument_details: instrument,
                billing_information: billingInformation,
            },
        ],
    };
};

const _payloadForUserInstrument = (userInstrument: UserInstrument) => ({
    payment_methods: [
        {
            payment_instrument_details: deepKeysToSnake(userInstrument),
        },
    ],
});

/**
 * Returns the payload used to pay with a virtual incentives card
 */
const _payloadForVirtualIncentives = ({
    virtualIncentives,
    totalAmountInCents,
}: {
    virtualIncentives: Array<VirtualIncentive>;
    totalAmountInCents: number;
}) => {
    if (virtualIncentives.length === 0) {
        throw new Error('Virtual incentives array was empty');
    }

    const currency = virtualIncentives[0].balance.currency;
    const totalValue = Math.min(
        getSelectedVirtualIncentivesAggregatedTotal(virtualIncentives),
        totalAmountInCents,
    );

    return {
        payment_methods: [
            {
                payment_instrument_details: {
                    instrument: {
                        instrument_type: InstrumentType.VIRTUAL_INCENTIVES_CARD,
                    },
                    instrument_type: PaymentMethodType.USER_INSTRUMENT,
                },
                amount: {
                    currency,
                    value: totalValue,
                },
            },
        ],
    };
};

const _getPayloadForSplitPayment = ({
    currency,
    totalAmountInCents,
    virtualIncentives,
    paymentMethodBodyBuilder,
}: {
    currency: string;
    totalAmountInCents: number;
    virtualIncentives: Array<VirtualIncentive>;
    paymentMethodBodyBuilder: () => {
        payment_methods: Array<Record<string, unknown>>;
    };
}) => {
    const payloadForVirtualIncentives = _payloadForVirtualIncentives({
        virtualIncentives,
        totalAmountInCents,
    });

    const amountInCentsPayedWithVirtualIncentives =
        payloadForVirtualIncentives.payment_methods[0].amount.value;
    const amountInCentsPayedWithAdditionalPaymentMethod =
        totalAmountInCents - amountInCentsPayedWithVirtualIncentives;
    const additionalPaymentMethodPayload = paymentMethodBodyBuilder();

    const payloadForSplitPaymentMethods = [
        ...payloadForVirtualIncentives.payment_methods,
        {
            ...additionalPaymentMethodPayload.payment_methods[0],
            amount: {
                currency,
                value: amountInCentsPayedWithAdditionalPaymentMethod,
            },
        },
    ];

    return {
        payment_methods: payloadForSplitPaymentMethods,
    };
};

const _getPayloadForSinglePayment = ({
    currency,
    totalAmountInCents,
    virtualIncentives,
    paymentMethodBodyBuilder,
}: {
    currency: string;
    totalAmountInCents: number;
    virtualIncentives?: Array<VirtualIncentive>;
    paymentMethodBodyBuilder: () => {
        payment_methods: Array<Record<string, unknown>>;
    };
}) => {
    return virtualIncentives
        ? _payloadForVirtualIncentives({
              virtualIncentives,
              totalAmountInCents,
          })
        : {
              payment_methods: [
                  {
                      ...paymentMethodBodyBuilder().payment_methods[0],
                      amount: {
                          currency,
                          value: totalAmountInCents,
                      },
                  },
              ],
          };
};

type StronglyTypedPaymentData =
    | {
          paymentMethod: PaymentMethodType.USER_INSTRUMENT;
          userInstrument: UserInstrument;
      }
    | {
          paymentMethod: PaymentMethodType.MAESTRO_BANCONTACT;
          bancontact: BancontactPaymentData;
      }
    | {
          paymentMethod: PaymentMethodType.APPLE_PAY;
          payment: ApplePayPaymentData;
      }
    | {
          paymentMethod: PaymentMethodType.GOOGLE_PAY;
          payment: GooglePayPaymentData;
      }
    | {
          paymentMethod: PaymentMethodType.BOLETO_BANCARIO;
          payment: BoletoBancarioPaymentData;
      }
    | {
          paymentMethod:
              | PaymentMethodType.RAPIPAGO
              | PaymentMethodType.PAGOFACIL
              | PaymentMethodType.BARCODE
              | PaymentMethodType.OXXO;
          payment: BarcodePaymentData;
      }
    | {
          paymentMethod:
              | PaymentMethodType.NOT_SELECTED
              | PaymentMethodType.CASH
              | PaymentMethodType.CHECK
              | PaymentMethodType.FPP_PAYPAL
              | PaymentMethodType.INVOICE
              | PaymentMethodType.SOFORT;
      }
    | {
          paymentMethod: PaymentMethodType.CREDIT | PaymentMethodType.AUTHNET;
          payment: CreditCardPaymentData;
          countryCode: string;
          currency: string;
          isNOS?: boolean;
      }
    | {
          paymentMethod: PaymentMethodType.IDEAL;
          payment: IdealPaymentData;
      }
    | {
          paymentMethod: PaymentMethodType.NONCE_BRAINTREE;
          payment: PaypalEPPPaymentData;
      }
    | {
          paymentMethod: PaymentMethodType.SEPA_DIRECT_DEBIT;
          payment: SepaPaymentData;
      }
    | {
          paymentMethod: PaymentMethodType.STRIPE_CONFIRMATION_TOKEN;
          payment: StripeConfirmationTokenPaymentData;
      };

const getPaymentMethodBodyBuilder = (
    params: StronglyTypedPaymentData,
): { payment_methods: Array<{ payment_instrument_details: unknown }> } => {
    switch (params.paymentMethod) {
        case PaymentMethodType.APPLE_PAY:
            return _payloadForApplePay(params.payment);
        case PaymentMethodType.GOOGLE_PAY:
            return _payloadForGooglePay(params.payment);
        case PaymentMethodType.BOLETO_BANCARIO:
            return _payloadForBoletoBancario(params.payment);
        case PaymentMethodType.RAPIPAGO:
        case PaymentMethodType.PAGOFACIL:
        case PaymentMethodType.OXXO:
            return _payloadForBarcode({
                ...params.payment,
                variant: params.paymentMethod,
            });
        case PaymentMethodType.BARCODE:
            return _payloadForBarcode(params.payment);
        case PaymentMethodType.CASH:
        case PaymentMethodType.CHECK:
        case PaymentMethodType.FPP_PAYPAL:
        case PaymentMethodType.INVOICE:
            return _defaultPayload(params.paymentMethod);
        case PaymentMethodType.CREDIT:
            return _payloadForCredit({
                countryCode: params.countryCode,
                payment: params.payment,
                currency: params.currency,
                isNOS: params.isNOS,
            });
        case PaymentMethodType.AUTHNET:
            return _payloadForAuthnet({
                countryCode: params.countryCode,
                payment: params.payment,
                currency: params.currency,
            });
        case PaymentMethodType.IDEAL:
            return _payloadForIdeal(params.payment);
        case PaymentMethodType.MAESTRO_BANCONTACT:
            return _payloadForMaestroBancontact(params.bancontact);
        case PaymentMethodType.NONCE_BRAINTREE:
            return _payloadForNonceBraintree(params.payment);
        case PaymentMethodType.SEPA_DIRECT_DEBIT:
            return _payloadForSepa(params.payment);
        case PaymentMethodType.SOFORT:
            return _payloadForSofort();
        case PaymentMethodType.USER_INSTRUMENT:
            return _payloadForUserInstrument(params.userInstrument);
        case PaymentMethodType.STRIPE_CONFIRMATION_TOKEN:
            return _payloadForStripeConfirmationToken(params.payment);
        case PaymentMethodType.NOT_SELECTED:
        default:
            return { payment_methods: [] };
    }
};

const _getPayloadForPaymentMethod = ({
    virtualIncentives,
    totalAmountInCents,
    ...params
}: StronglyTypedPaymentData & {
    virtualIncentives?: Array<VirtualIncentive>;
    currency: string;
    totalAmountInCents: number;
    isNOS?: boolean;
}) => {
    const paymentMethodBodyBuilder = () =>
        getPaymentMethodBodyBuilder({
            ...params,
        });

    if (!virtualIncentives) {
        return _getPayloadForSinglePayment({
            currency: params.currency,
            totalAmountInCents,
            virtualIncentives,
            paymentMethodBodyBuilder,
        });
    }

    const amountInCentsPayedWithVirtualIncentives =
        getSelectedVirtualIncentivesAggregatedTotal(virtualIncentives);
    const isAdditionalPaymentMethodNeededToPayCosts =
        totalAmountInCents > amountInCentsPayedWithVirtualIncentives;

    return isAdditionalPaymentMethodNeededToPayCosts
        ? _getPayloadForSplitPayment({
              currency: params.currency,
              paymentMethodBodyBuilder,
              totalAmountInCents,
              virtualIncentives,
          })
        : _getPayloadForSinglePayment({
              currency: params.currency,
              totalAmountInCents,
              virtualIncentives,
              paymentMethodBodyBuilder,
          });
};

const hasSomeUndefinedValue = (object: Record<string, any>): boolean =>
    !!Object.keys(object).find((key) =>
        isPlainObject(object[key]) || isArray(object[key])
            ? hasSomeUndefinedValue(object[key])
            : object[key] === undefined,
    );

const PAYMENT_METHODS_REQUIRING_INFO_FOR_ORDER = {
    // Requires identification method
    [PAYMENT_METHODS.BOLETO_BANCARIO]: true,
    // Requires identification method
    [PAYMENT_METHODS.RAPIPAGO]: true,
    // Requires identification method
    [PAYMENT_METHODS.PAGOFACIL]: true,
    // Requires identification method
    [PAYMENT_METHODS.BARCODE]: true,
    // Requires card number, expiration date, etc
    [PAYMENT_METHODS.CREDIT]: true,
    // Requires card number, expiration date, etc
    [PAYMENT_METHODS.AUTHNET]: true,
    // Requires bank id
    [PAYMENT_METHODS.IDEAL]: true,
    // Requires card number, expiration date, etc
    [PAYMENT_METHODS.MAESTRO_BANCONTACT]: true,
    // Requires PayPal Braintree nonce
    [PAYMENT_METHODS.NONCE_BRAINTREE]: false,
    // Requires owner name and iban
    [PAYMENT_METHODS.SEPA_DIRECT_DEBIT]: true,
    // Requires user instrument
    [PAYMENT_METHODS.USER_INSTRUMENT]: true,
    // Requires confirmation token id
    [PAYMENT_METHODS.STRIPE_CONFIRMATION_TOKEN]: true,
};

interface PaymentFormData
    extends IdealPaymentData,
        ApplePayPaymentData,
        GooglePayPaymentData,
        PaypalEPPPaymentData,
        BoletoBancarioPaymentData,
        BarcodePaymentData,
        SepaPaymentData,
        CreditCardPaymentData,
        StripeConfirmationTokenPaymentData {}

export const isAllInfoRequiredToSavePaymentMethodAvailable = ({
    countryCode,
    bancontact,
    payment,
    paymentMethod,
    totalAmountInCents,
    userInstrument,
    virtualIncentives,
    currency,
}: {
    bancontact: BancontactPaymentData;
    countryCode: string;
    currency: string;
    payment: PaymentFormData;
    paymentMethod: PaymentMethodType;
    totalAmountInCents: number;
    userInstrument: UserInstrument;
    virtualIncentives: Array<VirtualIncentive>;
}) => {
    const body = _getPayloadForPaymentMethod({
        bancontact,
        countryCode,
        currency,
        payment,
        paymentMethod,
        totalAmountInCents,
        userInstrument,
        virtualIncentives,
    });

    return PAYMENT_METHODS_REQUIRING_INFO_FOR_ORDER[
        paymentMethod || PAYMENT_METHODS.CREDIT
    ]
        ? !hasSomeUndefinedValue(body)
        : true;
};

export type PaymentData = {
    bancontact: BancontactPaymentData;
    payment: PaymentFormData;
    paymentMethod: PaymentMethodType;
    totalAmountInCents: number;
    userInstrument: UserInstrument;
    virtualIncentives: Array<VirtualIncentive>;
};

/**
 * Return a `fetch` Promise to save payment methods
 *
 * @param orderId order id to be updated
 * @param countryCode
 * @param paymentData form data provided by checkout form
 * @param currency
 * @param isNOS boolean denoting order service
 */
export const savePaymentMethods = ({
    orderId,
    countryCode,
    paymentData,
    currency,
    isNOS,
}: {
    orderId: string;
    countryCode: string;
    paymentData: PaymentData;
    currency: string;
    isNOS: boolean;
}) => {
    const body = _getPayloadForPaymentMethod({
        countryCode,
        currency,
        ...paymentData,
        isNOS,
    });

    return _fetchV3WithOrderErrorHandling(
        _getSavePaymentMethodUrl(orderId, paymentData.paymentMethod),
        {
            method: 'POST',
            headers: {
                ...getOrderFlowHeaders(getTokens()),
                // TODO: EB-136121 Move this header to a common place for all requests
                'X-Checkout-AppVersion': packageJson.version,
            },
            body: JSON.stringify(body),
        },
    );
};

/**
 * Removes order's payment methods
 *
 * @param orderId order id to be updated
 */
export const clearPaymentMethods = (orderId: string) =>
    _fetchV3WithOrderErrorHandling(
        _getClearPaymentMethodUrl(orderId, PAYMENT_METHOD_CLEAR_OPERATION),
        {
            method: 'DELETE',
            headers: {
                ...getOrderFlowHeaders(getTokens()),
                // TODO: EB-136121 Move this header to a common place for all requests
                'X-Checkout-AppVersion': packageJson.version,
            },
        },
    );
